/**
 * `/foundations` — the design system rendered from real components.
 *
 * This page is Phase 0's exit criterion (§14): "the design system renders the
 * Foundations sheet from real components". It is deliberately not a storybook — it is
 * the same `Foundations.dc.html` artboard, built from `@portal/ui`, so a token drift or
 * a broken chip is visible on one screen instead of being discovered in the workspace.
 */

import type { CSSProperties, ReactNode } from 'react';

import {
  Avatar,
  ItemRow,
  MarkerChip,
  PermissionButton,
  ProvenanceChip,
  ScoreRing,
  SectionBar,
  StateChip,
  maturityLabel,
} from '@portal/ui';
import type { ItemState, MaturityLevel, Obligation } from '@portal/domain';

const STATES: Array<{ state: ItemState; meaning: string }> = [
  { state: 'PENDING', meaning: 'Not yet examined. The default for every item in a new audit.' },
  { state: 'VERIFIED', meaning: 'Evidence seen and accepted. Counts toward the score.' },
  { state: 'PARTIAL', meaning: 'Partly in place. Requires a gap note; does not count.' },
  { state: 'REJECTED', meaning: 'Absent or inadequate. Raises a finding with owner and deadline.' },
  {
    state: 'NOT_APPLICABLE',
    meaning: 'Out of scope by context. Contextual items only; leaves both sides of the score.',
  },
  {
    state: 'WAIVED',
    meaning: 'Accepted as an exception. Needs an approver, a reason and an expiry; stays in the denominator.',
  },
];

const MARKERS: Array<{ obligation: Obligation; isContextual: boolean; meaning: string }> = [
  { obligation: 'MANDATORY', isContextual: false, meaning: 'In the score denominator; blocks the gate.' },
  { obligation: 'RECOMMENDED', isContextual: false, meaning: 'Reported separately, never blocking.' },
  {
    obligation: 'MANDATORY',
    isContextual: true,
    meaning: 'Applies only when its condition is true — then it is mandatory.',
  },
  {
    obligation: 'RECOMMENDED',
    isContextual: true,
    meaning: 'Applies only when its condition is true — then it is recommended.',
  },
];

const LEVELS: MaturityLevel[] = ['L1', 'L2', 'L3', 'L4'];

function Card({ title, note, children }: { title: string; note?: string; children: ReactNode }) {
  return (
    <section
      style={{
        background: 'var(--sf)',
        border: '1px solid var(--bd)',
        borderRadius: 'var(--r-lg)',
        padding: '16px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, font: '600 13px/1.3 var(--font-sans)' }}>{title}</h2>
        {note !== undefined && (
          <span style={{ fontSize: 11, color: 'var(--mu)', lineHeight: 1.5 }}>{note}</span>
        )}
      </div>
      {children}
    </section>
  );
}

const LABEL: CSSProperties = {
  fontSize: 'var(--fs-label)',
  fontWeight: 600,
  letterSpacing: 'var(--tracking-label)',
  textTransform: 'uppercase',
  color: 'var(--mu)',
};

export function FoundationsRoute() {
  return (
    <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 1180 }}>
      <header style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <span style={{ ...LABEL, color: 'var(--ac)' }}>Design foundations</span>
        <h1 style={{ margin: 0, font: '600 24px/1.1 var(--font-sans)', letterSpacing: '-0.015em' }}>
          Maintenance Readiness Console
        </h1>
        <p style={{ margin: 0, fontSize: 12, color: 'var(--mu)', lineHeight: 1.55, maxWidth: 660 }}>
          Every component on this page is the one the product uses. If a chip here looks wrong, the
          workspace is wrong too. Tokens come from <code className="mono">@portal/ui/tokens.css</code>,
          copied verbatim from the artboards.
        </p>
      </header>

      <Card
        title="The six item states"
        note="The whole product turns on this enum. Never colour alone: every state carries a glyph and a word."
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {STATES.map(({ state, meaning }) => (
            <div key={state} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <StateChip state={state} fixedWidth />
              <span style={{ fontSize: 11.5, color: 'var(--mu)', lineHeight: 1.5 }}>{meaning}</span>
            </div>
          ))}
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 16 }}>
        <Card
          title="Obligation markers"
          note="[C] is a modifier on [M] or [R], not a third marker (§4.2) — so there are four chips, not three."
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {MARKERS.map(({ obligation, isContextual, meaning }) => (
              <div
                key={`${obligation}-${String(isContextual)}`}
                style={{ display: 'flex', alignItems: 'center', gap: 12 }}
              >
                <span style={{ width: 56, flex: '0 0 56px' }}>
                  <MarkerChip obligation={obligation} isContextual={isContextual} />
                </span>
                <span style={{ fontSize: 11.5, color: 'var(--mu)', lineHeight: 1.5 }}>{meaning}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Provenance and freshness" note="Where a value came from is never hidden.">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <ProvenanceChip provenance={{ kind: 'inherited', auditNumber: 14 }} />
              <span style={{ fontSize: 11.5, color: 'var(--mu)' }}>
                A dashed border always means “not verified in this audit”.
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <ProvenanceChip provenance={{ kind: 'auto-pass', ageLabel: '2 h' }} />
              <span style={{ fontSize: 11.5, color: 'var(--mu)' }}>A probe agrees, with its age.</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <ProvenanceChip provenance={{ kind: 'auto-fail' }} />
              <span style={{ fontSize: 11.5, color: 'var(--mu)' }}>
                The probe diverges. It never overwrites — it asks.
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <ProvenanceChip provenance={{ kind: 'expiring', daysRemaining: 12 }} />
              <ProvenanceChip provenance={{ kind: 'expired' }} />
              <span style={{ fontSize: 11.5, color: 'var(--mu)' }}>
                Structural 365 d · configuration 180 d · operational 90 d.
              </span>
            </div>
          </div>
        </Card>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Card title="Score components" note="Mandatory items only. A null score renders “—”, never 0%.">
          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            <ScoreRing percent={87} maturity="L3" size={96} />
            <ScoreRing percent={null} size={96} caption="NO ITEMS" />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <span className="mono" style={{ width: 12, fontWeight: 600, fontSize: 11 }}>
                  B
                </span>
                <SectionBar counts={{ verified: 25, partial: 3, rejected: 1, waived: 1, pending: 0 }} />
                <span className="mono" style={{ fontSize: 11, color: 'var(--mu)' }}>
                  25/30
                </span>
              </div>
              <span style={{ fontSize: 11, color: 'var(--mu)', lineHeight: 1.5 }}>
                A stacked bar says <em>why</em> a section is short, instead of one flat percentage.
              </span>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {LEVELS.map((level) => (
                  <span key={level} style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--mu)' }}>
                    {maturityLabel(level)}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Card>

        <Card title="Controls" note="32 px on desktop; 44 px minimum on the mobile views.">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <PermissionButton variant="primary">New audit</PermissionButton>
            <PermissionButton>Export</PermissionButton>
            <PermissionButton variant="destructive">Reject &amp; open finding</PermissionButton>
            <PermissionButton variant="primary" disabledReason="Only the Delivery Manager can approve">
              Authorise go-live
            </PermissionButton>
          </div>
          <p style={{ margin: 0, fontSize: 11, color: 'var(--mu)', lineHeight: 1.5 }}>
            A disabled control is shown, greyed, with a lock and a reason on hover (§8.4). An auditor
            who cannot see the Approve button assumes the tool is broken.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={LABEL}>Owners</span>
            {['SB', 'JR', 'MK', 'AP', 'LN', 'CF'].map((initials) => (
              <Avatar key={initials} initials={initials} size={22} />
            ))}
          </div>
        </Card>
      </div>

      <Card
        title="Item row anatomy"
        note="38 px tall, eight fixed slots, in the same order on every screen."
      >
        <div style={{ border: '1px solid var(--bd)', borderRadius: 'var(--r-lg)', overflow: 'hidden' }}>
          <ItemRow
            ref_="A-01"
            obligation="MANDATORY"
            isContextual={false}
            statement="A single authoritative Git repository exists for the application"
            state="VERIFIED"
            provenance={[
              { kind: 'inherited', auditNumber: 14 },
              { kind: 'auto-pass', ageLabel: '2 h' },
            ]}
            evidenceCount={1}
            owner={{ initials: 'MK', displayName: 'M. Keller' }}
          />
          <ItemRow
            ref_="A-05"
            obligation="MANDATORY"
            isContextual={false}
            statement="No secrets are committed, and a secret-scanning step is active in the build"
            state="PARTIAL"
            rollUp={{ verified: 2, considered: 3, state: 'PARTIAL' }}
            evidenceCount={2}
            owner={{ initials: 'JR', displayName: 'J. Roth' }}
            selected
          />
          <ItemRow
            ref_="A-18"
            obligation="MANDATORY"
            isContextual
            statement="If personal data is processed: the processing record (GDPR Art. 30) exists"
            state="PENDING"
            owner={{ initials: 'CF', displayName: 'C. Fischer' }}
          />
          <ItemRow
            ref_="A-06"
            obligation="RECOMMENDED"
            isContextual={false}
            statement="Commit signing or a verified-author policy is enabled"
            state="WAIVED"
            provenance={[{ kind: 'expiring', daysRemaining: 90 }]}
            owner={{ initials: 'AP', displayName: 'A. Petit' }}
          />
        </div>
        <p style={{ margin: 0, fontSize: 11, color: 'var(--mu)', lineHeight: 1.5 }}>
          Reference · obligation · statement · provenance · repository roll-up · evidence count ·
          owner · state. The statement truncates; it never wraps.
        </p>
      </Card>
    </div>
  );
}
